import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader2, RefreshCw, Activity, AlertCircle, CheckCircle2, StopCircle } from "lucide-react";

// Read-only admin surface for AI/LLM call telemetry. Consumes only the
// two existing admin endpoints — no mutations. Explicitly labels
// token/cost fields as unknown whenever the provider abstraction does
// not surface them, so the operator can never mistake fabrication for
// measurement.

const WINDOW_OPTIONS = [
  { value: 1, label: "Son 1 saat" },
  { value: 6, label: "Son 6 saat" },
  { value: 24, label: "Son 24 saat" },
  { value: 24 * 7, label: "Son 7 gün" },
];

// Route-family filter for both the summary derivation and the recent
// events table. Kept as a small closed set — free-text filtering would
// let operators query arbitrary routes and would drift the shape.
const ROUTE_FILTERS = [
  { value: "all", label: "Tümü", match: () => true },
  { value: "chat", label: "Sohbet", match: (r) => typeof r === "string" && r.startsWith("/api/chat/") },
  { value: "voice-stt", label: "Sesli STT", match: (r) => r === "/api/voice/transcribe" },
];

const VOICE_STT_ROUTE = "/api/voice/transcribe";

// Provenance / origin filter — closed set. "Bilinmiyor" bucket covers
// legacy rows written before commit 82de05b (they carry no `origin`
// field) and any future null/unknown value the backend might emit.
// Historical rows are NEVER coerced into an origin category.
//
// SCOPE OF THIS FILTER: applies ONLY to per-event / sample views that
// live client-side (Son Çağrılar table and the two client-derived
// Voice STT sub-panels). The aggregate summary cards and Rota Dağılımı
// table come from summarize_ai_telemetry() which is origin-BLIND and
// caps its own window at 5 000 rows; we must NOT recolour those from
// the 100-row recent sample or we'd invent numbers.
const ORIGIN_FILTERS = [
  { value: "all",       label: "Tümü",       match: () => true },
  { value: "organic",   label: "Organik",    match: (o) => o === "organic" },
  { value: "demo",      label: "Demo",       match: (o) => o === "demo" },
  { value: "test",      label: "Test",       match: (o) => o === "test" },
  { value: "unknown",   label: "Bilinmiyor", match: (o) => !o || (o !== "organic" && o !== "demo" && o !== "test" && o !== "admin") },
  { value: "admin",     label: "Admin",      match: (o) => o === "admin" },
];

const OUTCOME_META = {
  success: { label: "Başarılı", icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-100" },
  failed: { label: "Başarısız", icon: AlertCircle, cls: "text-rose-700 bg-rose-100" },
  aborted: { label: "Durduruldu", icon: StopCircle, cls: "text-amber-700 bg-amber-100" },
};

function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; }
}

function OutcomeBadge({ outcome }) {
  const meta = OUTCOME_META[outcome] || { label: outcome || "—", icon: Activity, cls: "text-zinc-700 bg-zinc-100" };
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${meta.cls}`}
      data-testid={`ai-outcome-${outcome || "unknown"}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="mt-2 font-display text-3xl font-bold text-zinc-900">
        {value === null || value === undefined ? "—" : value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

export default function AdminAiTelemetry() {
  const [hours, setHours] = useState(24);
  const [routeFilter, setRouteFilter] = useState("all");
  const [originFilter, setOriginFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  async function loadAll() {
    setRefreshing(true);
    setErr(null);
    try {
      const [s, e] = await Promise.all([
        api.get(`/admin/ai-telemetry/summary?hours=${hours}`).then((r) => r.data),
        api.get(`/admin/ai-telemetry/recent?limit=100`).then((r) => r.data),
      ]);
      setSummary(s);
      setEvents(e?.events || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  const byRoute = summary?.by_route || {};
  const byModel = summary?.by_model || {};
  const topErrors = summary?.top_errors || [];
  const tokenAvailable = summary?.token_count_available;

  const activeRouteFilter = ROUTE_FILTERS.find((f) => f.value === routeFilter) || ROUTE_FILTERS[0];
  const activeOriginFilter = ORIGIN_FILTERS.find((f) => f.value === originFilter) || ORIGIN_FILTERS[0];

  // Voice STT scoped derivations — the aggregate cards (total/success/
  // failed) come from summary.by_route which is origin-BLIND on the
  // server side; the per-event latency + day histogram are derived
  // client-side from the recent-100 sample and DO respect the origin
  // chip. This is documented in-UI so operators do not misread them.
  const voiceRoute = byRoute[VOICE_STT_ROUTE] || null;
  const voiceEventsAll = (events || []).filter((e) => e.route === VOICE_STT_ROUTE);
  const voiceEvents = voiceEventsAll.filter((e) => activeOriginFilter.match(e.origin));
  const voiceSuccessLatencies = voiceEvents
    .filter((e) => e.outcome === "success")
    .map((e) => Number(e.latency_ms) || 0);
  const voiceAvgLatencyMs = voiceSuccessLatencies.length
    ? Math.round(voiceSuccessLatencies.reduce((a, b) => a + b, 0) / voiceSuccessLatencies.length)
    : null;
  const voiceByDay = voiceEvents.reduce((acc, e) => {
    const d = (e.at || "").slice(0, 10);
    if (!d) return acc;
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});
  const voiceByDaySorted = Object.entries(voiceByDay).sort(([a], [b]) => (a < b ? 1 : -1));

  // Recent-events table filtering — route AND origin compose.
  const filteredEvents = events
    ? events.filter((e) => activeRouteFilter.match(e.route) && activeOriginFilter.match(e.origin))
    : null;
  // Rota Dağılımı stays global (server aggregate). Only the route chip
  // narrows it — never the origin chip.
  const filteredByRoute = Object.entries(byRoute).filter(([r]) => activeRouteFilter.match(r));

  // Per-origin badge counts from the currently loaded recent sample —
  // helpful "how much of what I loaded is X" signal only. Explicitly
  // labelled "(örnekte)" in the UI so operators do not read it as
  // an aggregate.
  const originCounts = ORIGIN_FILTERS.reduce((acc, f) => {
    if (f.value === "all") return acc;
    acc[f.value] = (events || []).filter((e) => f.match(e.origin)).length;
    return acc;
  }, {});

  return (
    <AppShell variant="admin">
      <div className="max-w-6xl mx-auto p-5 lg:p-10" data-testid="admin-ai-telemetry">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">İzleme</div>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
              AI Telemetri
            </h1>
            <p className="mt-2 text-zinc-600 max-w-2xl">
              Sohbet AI çağrılarının başarı/başarısızlık, gecikme ve rota/model dağılımı.
              Token ve maliyet alanları yalnızca sağlayıcı gerçek değer döndürdüğünde gösterilir —
              tahmine dayalı bir sayı üretmiyoruz.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900"
              data-testid="ai-telemetry-window"
            >
              {WINDOW_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={loadAll}
              disabled={refreshing}
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900 hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="ai-telemetry-refresh"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Yenile
            </button>
          </div>
        </div>

        {err && <div className="mt-6 rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">{err}</div>}

        {!summary && !err && (
          <div className="mt-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
        )}

        {summary && (
          <>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Toplam çağrı" value={summary.total_requests} testid="stat-total" />
              <StatCard label="Başarılı" value={summary.success_count} testid="stat-success" />
              <StatCard label="Başarısız" value={summary.failed_count} testid="stat-failed" />
              <StatCard label="Durdurulan" value={summary.aborted_count} testid="stat-aborted" />
            </div>

            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Ort. gecikme (ms)"
                value={summary.avg_latency_ms_success}
                hint="yalnızca başarılı çağrılar"
                testid="stat-avg-latency"
              />
              <StatCard
                label="p50 gecikme (ms)"
                value={summary.p50_latency_ms_success}
                testid="stat-p50-latency"
              />
              <StatCard
                label="p95 gecikme (ms)"
                value={summary.p95_latency_ms_success}
                testid="stat-p95-latency"
              />
              <StatCard
                label="Token verisi"
                value={tokenAvailable ? "Mevcut" : "Yok"}
                hint={tokenAvailable
                  ? `Toplam prompt: ${summary.sum_prompt_tokens ?? "—"} · completion: ${summary.sum_completion_tokens ?? "—"}`
                  : "Sağlayıcı bu sürümde token/maliyet döndürmüyor"}
                testid="stat-token-availability"
              />
            </div>

            <div className="mt-8 rounded-2xl border border-[#1E3A8A]/15 bg-[#1E3A8A]/[0.03] p-5" data-testid="voice-stt-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#1E3A8A] font-semibold">
                    Sesli STT (yedek yol)
                    <span className="ml-2 normal-case tracking-normal text-[10px] text-zinc-500 italic">
                      (toplam kartlar: tüm kaynaklar)
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-600 max-w-lg">
                    Yalnızca <span className="font-mono">POST {VOICE_STT_ROUTE}</span> çağrıları — tarayıcının
                    kendi ses tanıma servisini kullanamadığında devreye giren sunucu yedek yolu.
                  </div>
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                  label="Yedek istek"
                  value={voiceRoute ? voiceRoute.total : 0}
                  hint={voiceRoute ? undefined : "Bu pencerede kayıt yok"}
                  testid="voice-stat-total"
                />
                <StatCard
                  label="Başarılı"
                  value={voiceRoute ? voiceRoute.success : 0}
                  hint={
                    voiceRoute && voiceRoute.total
                      ? `${Math.round((voiceRoute.success / voiceRoute.total) * 100)}% başarı`
                      : undefined
                  }
                  testid="voice-stat-success"
                />
                <StatCard
                  label="Hata"
                  value={voiceRoute ? voiceRoute.failed : 0}
                  hint={
                    voiceRoute && voiceRoute.total
                      ? `${Math.round((voiceRoute.failed / voiceRoute.total) * 100)}% hata`
                      : undefined
                  }
                  testid="voice-stat-failed"
                />
                <StatCard
                  label={`Ort. gecikme${originFilter === "all" ? "" : ` (${activeOriginFilter.label})`}`}
                  value={voiceAvgLatencyMs !== null ? `${voiceAvgLatencyMs} ms` : "—"}
                  hint={
                    voiceSuccessLatencies.length
                      ? `${voiceSuccessLatencies.length} başarılı yanıttan (örnekte)`
                      : "Başarılı yedek çağrı yok"
                  }
                  testid="voice-stat-avg-latency"
                />
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <div
                  className="rounded-xl border border-zinc-200 bg-white p-4"
                  data-testid="voice-cost-note"
                >
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                    Maliyet / token
                  </div>
                  <div className="mt-1 font-semibold text-zinc-900">Ölçülemiyor</div>
                  <div className="mt-1 text-xs text-zinc-500 leading-relaxed">
                    Sağlayıcı bu sürümde ses uzunluğu veya maliyet döndürmüyor; tahmine dayalı
                    değer üretilmez. Boş göstermek doğru olur.
                  </div>
                </div>
                <div
                  className="rounded-xl border border-zinc-200 bg-white p-4"
                  data-testid="voice-day-volume"
                >
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                    Gün bazında hacim (son çağrılardan)
                    {originFilter !== "all" && (
                      <span className="ml-1 normal-case tracking-normal text-zinc-400">
                        · {activeOriginFilter.label}
                      </span>
                    )}
                  </div>
                  {voiceByDaySorted.length === 0 ? (
                    <div className="mt-1 text-xs text-zinc-500">
                      Bu pencerede yedek yol çağrısı yok.
                    </div>
                  ) : (
                    <ul className="mt-1 space-y-1 text-xs">
                      {voiceByDaySorted.slice(0, 7).map(([day, count]) => (
                        <li key={day} className="flex items-center justify-between">
                          <span className="font-mono text-zinc-700">{day}</span>
                          <span className="text-zinc-900 font-medium">{count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 text-[10px] text-zinc-500 italic">
                    Not: Bu tablo son 100 çağrıdan türetilir; yüksek hacimde tam gün toplamı olmayabilir.
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="ai-route-filter">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                Rota süzgeci
              </span>
              {ROUTE_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setRouteFilter(f.value)}
                  className={`h-8 px-3 rounded-full border text-xs transition-colors ${
                    routeFilter === f.value
                      ? "bg-[#1E3A8A] border-[#1E3A8A] text-white"
                      : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
                  }`}
                  data-testid={`ai-route-filter-${f.value}`}
                  aria-pressed={routeFilter === f.value}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="ai-origin-filter" role="group" aria-label="Kaynak süzgeci">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                Kaynak süzgeci
              </span>
              {ORIGIN_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setOriginFilter(f.value)}
                  className={`h-8 px-3 rounded-full border text-xs transition-colors inline-flex items-center gap-1.5 ${
                    originFilter === f.value
                      ? "bg-[#1E3A8A] border-[#1E3A8A] text-white"
                      : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
                  }`}
                  data-testid={`ai-origin-filter-${f.value}`}
                  aria-pressed={originFilter === f.value}
                >
                  {f.label}
                  {f.value !== "all" && (
                    <span
                      className={`text-[10px] px-1.5 rounded-full ${
                        originFilter === f.value
                          ? "bg-white/20 text-white"
                          : "bg-zinc-100 text-zinc-600"
                      }`}
                      data-testid={`ai-origin-filter-${f.value}-count`}
                    >
                      {originCounts[f.value] || 0}
                    </span>
                  )}
                </button>
              ))}
              <span
                className="text-[10px] text-zinc-500 italic ml-1 basis-full"
                data-testid="ai-origin-filter-scope-note"
              >
                Kaynak süzgeci yalnızca Son Çağrılar ve Sesli STT'nin örnek türevlerine uygulanır;
                yukarıdaki toplam kartlar ve Rota Dağılımı sunucu-agregasından gelir ve tüm
                kaynakları kapsar. Rozet sayıları yüklü örneğe göredir.
              </span>
            </div>

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="ai-by-route">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold" data-testid="ai-by-route-title">
                  Rota Dağılımı
                  <span className="ml-2 normal-case tracking-normal text-[10px] text-zinc-400 italic">(tüm kaynaklar)</span>
                </div>
                {filteredByRoute.length === 0 && <div className="mt-3 text-xs text-zinc-500">Kayıt yok.</div>}
                {filteredByRoute.length > 0 && (
                  <table className="mt-3 w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                        <th className="py-1.5 font-semibold">Rota</th>
                        <th className="py-1.5 font-semibold text-right">Başarılı</th>
                        <th className="py-1.5 font-semibold text-right">Hata</th>
                        <th className="py-1.5 font-semibold text-right">Durdu</th>
                        <th className="py-1.5 font-semibold text-right">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredByRoute.map(([route, c]) => (
                        <tr key={route} className="border-b border-zinc-50 last:border-0">
                          <td className="py-1.5 font-mono text-xs text-zinc-700">{route}</td>
                          <td className="py-1.5 text-right text-emerald-700">{c.success}</td>
                          <td className="py-1.5 text-right text-rose-700">{c.failed}</td>
                          <td className="py-1.5 text-right text-amber-700">{c.aborted}</td>
                          <td className="py-1.5 text-right font-medium text-zinc-900">{c.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="ai-by-model">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Model Dağılımı</div>
                {Object.keys(byModel).length === 0 && <div className="mt-3 text-xs text-zinc-500">Kayıt yok.</div>}
                {Object.keys(byModel).length > 0 && (
                  <table className="mt-3 w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                        <th className="py-1.5 font-semibold">Model</th>
                        <th className="py-1.5 font-semibold text-right">Başarılı</th>
                        <th className="py-1.5 font-semibold text-right">Hata</th>
                        <th className="py-1.5 font-semibold text-right">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(byModel).map(([model, c]) => (
                        <tr key={model} className="border-b border-zinc-50 last:border-0">
                          <td className="py-1.5 font-mono text-xs text-zinc-700">{model}</td>
                          <td className="py-1.5 text-right text-emerald-700">{c.success}</td>
                          <td className="py-1.5 text-right text-rose-700">{c.failed}</td>
                          <td className="py-1.5 text-right font-medium text-zinc-900">{c.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {topErrors.length > 0 && (
              <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-5" data-testid="ai-top-errors">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">En Sık Hatalar</div>
                <ul className="mt-3 space-y-1 text-sm">
                  {topErrors.map((e) => (
                    <li key={e.error_class} className="flex items-center justify-between">
                      <span className="font-mono text-xs text-zinc-700">{e.error_class}</span>
                      <span className="text-zinc-500">{e.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-8 rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="ai-recent-events">
              <div className="px-5 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-2 flex-wrap">
                <span>Son Çağrılar (max 100)</span>
                {(routeFilter !== "all" || originFilter !== "all") && (
                  <span
                    className="normal-case tracking-normal text-[10px] text-[#1E3A8A]"
                    data-testid="ai-recent-active-filter-label"
                  >
                    süzgeç: {activeRouteFilter.label} · {activeOriginFilter.label}
                  </span>
                )}
              </div>
              {events === null && (
                <div className="p-8 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
              )}
              {events && filteredEvents.length === 0 && (
                <div className="p-8 text-center text-xs text-zinc-500" data-testid="ai-recent-empty">
                  {events.length === 0 ? "Kayıt yok." : "Bu süzgece uyan kayıt yok."}
                </div>
              )}
              {events && filteredEvents.length > 0 && (
                <div className="max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 sticky top-0">
                      <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                        <th className="px-4 py-2 font-semibold">Zaman</th>
                        <th className="px-4 py-2 font-semibold">Rota</th>
                        <th className="px-4 py-2 font-semibold">Kaynak</th>
                        <th className="px-4 py-2 font-semibold">Sonuç</th>
                        <th className="px-4 py-2 font-semibold text-right">Gecikme</th>
                        <th className="px-4 py-2 font-semibold text-right">Prompt/Yanıt</th>
                        <th className="px-4 py-2 font-semibold">Hata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEvents.map((ev) => (
                        <tr key={ev.id} className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50/50" data-testid="ai-recent-row" data-origin={ev.origin || "unknown"}>
                          <td className="px-4 py-2 text-xs text-zinc-500 font-mono">{fmtTs(ev.at)}</td>
                          <td className="px-4 py-2 text-xs font-mono text-zinc-700">{ev.route}</td>
                          <td className="px-4 py-2 text-xs">
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] bg-zinc-50 border-zinc-200 text-zinc-700"
                              data-testid="ai-recent-origin"
                            >
                              {(ORIGIN_FILTERS.find((f) => f.value !== "all" && f.match(ev.origin)) || { label: "Bilinmiyor" }).label}
                            </span>
                          </td>
                          <td className="px-4 py-2"><OutcomeBadge outcome={ev.outcome} /></td>
                          <td className="px-4 py-2 text-right text-xs text-zinc-700">{ev.latency_ms} ms</td>
                          <td className="px-4 py-2 text-right text-xs text-zinc-500">
                            {ev.prompt_char_count} / {ev.response_char_count}
                          </td>
                          <td className="px-4 py-2 text-xs text-rose-700 max-w-xs truncate">
                            {ev.error_class ? `${ev.error_class}${ev.error_message_head ? ": " + ev.error_message_head : ""}` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {!tokenAvailable && (
              <div className="mt-4 text-xs text-zinc-500 italic">
                Not: Mevcut sağlayıcı soyutlaması token veya maliyet bilgisi döndürmediği için bu alanlar
                boş gösteriliyor. Gerçek değer akmadan tahmin üretilmez.
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
